import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import { useHistory } from "react-router-dom";
import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
  Replay,
  Search,
  StorageOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { globalTextFieldSx, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { WizardStepper } from "pages/PayRoll/PayInputs/Shared/WizardStepper";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { readSheet } from "pages/PayRoll/Statutory/PF/PfUanImport";
import { IP_IMPORT_FIELDS, autoMapIpColumns, missingIpFields } from "./esiEligibilityData";
import { fetchEligibility, importIpNumbers } from "./esiEligibilityApi";

// Payroll > Statutory > ESI > Import IP numbers: Upload File -> Map Columns
// -> Preview & Import, same layout as the Pay Inputs imports (Leave,
// Overtime). The file is read and checked in the browser, then Map Columns
// > Next sends the rows that passed to POST eligibility/import-ip/ with
// dry_run (nothing saved) and the preview shows the server's answer. Error
// rows open an Edit panel to correct and re-verify. Import sends the valid
// rows for real and shows the result per row.

const STEPS = ["Upload File", "Map Columns", "Preview & Import"];
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_ROWS = 5000;
const TEMPLATE_SAMPLE_ROWS = 3;
const FIELD_KEYS = IP_IMPORT_FIELDS.map((field) => field.key);

const GUIDELINES = [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "Employee ID is the employee code (e.g. VST1005). Employee Name is only for checking and is not saved.",
  "IP Number is the 10-digit ESIC Insurance Number. Spaces and dashes are removed.",
  "Dispensary is optional. Covered in Current Period is Yes or No and is used only at go-live.",
  "One row per employee. Rows already saved with the same values are skipped, so importing twice changes nothing.",
  "Maximum 5,000 rows and 5 MB per file.",
];

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: designColors.teal },
  "& .MuiInputBase-input": { fontSize: 14 },
};
const tableHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, py: 1 };
const errorText = "#DC2626";

const formatBytes = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const textOf = (value) => (value === null || value === undefined ? "" : String(value).trim());
const shown = (value) => textOf(value) || "–";

// ---- Row checks ----

// File row -> { employee_id, employee_name, ip_number, dispensary, covered_in_current_period }.
const rawFromRow = (cells, mapping) => {
  const raw = Object.fromEntries(FIELD_KEYS.map((key) => [key, ""]));
  Object.entries(mapping).forEach(([index, key]) => {
    raw[key] = textOf(cells[index]);
  });
  return raw;
};

const coveredValue = (value) => {
  const text = textOf(value).toLowerCase();
  if (!text) return "";
  if (["y", "yes", "true", "1"].includes(text)) return "Yes";
  if (["n", "no", "false", "0"].includes(text)) return "No";
  return null;
};

// Browser check of one row. seen: employee codes already used by other rows.
// Returns { rowNumber, raw, errors, body } (body only when the row passed).
const checkRow = (rowNumber, raw, seen) => {
  const errors = [];
  const code = textOf(raw.employee_id);
  const ip = textOf(raw.ip_number).replace(/[\s-]/g, "");
  const covered = coveredValue(raw.covered_in_current_period);
  if (!code) errors.push("Employee ID is required.");
  else if (seen.has(code.toUpperCase())) errors.push("This Employee ID is already in another row of the file.");
  if (!ip) errors.push("IP Number is required.");
  else if (!/^\d{10}$/.test(ip)) errors.push("IP Number must be 10 digits.");
  if (covered === null) errors.push("Covered in Current Period must be Yes or No.");
  if (code) seen.add(code.toUpperCase());
  return {
    rowNumber,
    raw,
    errors,
    body: errors.length
      ? null
      : {
          employee_code: code,
          ip_number: ip,
          ...(textOf(raw.dispensary) ? { dispensary: textOf(raw.dispensary) } : {}),
          ...(covered ? { covered_in_current_period: covered } : {}),
        },
  };
};

// Sends the rows that passed the browser check (dry run unless told) and
// puts the server's per-row errors back on them. Server rows count from 1 in
// the order sent.
const serverCheck = async ({ domain, companyId, checked, dryRun = true }) => {
  const sent = checked.filter((item) => item.body);
  if (!sent.length) return { rows: checked, summary: null, sent };
  const summary = await importIpNumbers({ domain, companyId, rows: sent.map((item) => item.body), dryRun });
  const errorsBySent = {};
  summary.errors.forEach((item) => {
    (errorsBySent[item.row] = errorsBySent[item.row] || []).push(item.message || "Rejected by the server.");
  });
  const errorsByRow = {};
  sent.forEach((item, index) => {
    if (errorsBySent[index + 1]) errorsByRow[item.rowNumber] = errorsBySent[index + 1];
  });
  return {
    summary,
    sent,
    rows: checked.map((item) => (errorsByRow[item.rowNumber] ? { ...item, errors: errorsByRow[item.rowNumber], body: null } : item)),
    errorsByRow,
  };
};

// ---- Shared bits ----

const Card = ({ title, helper, action, children, sx }) => (
  <Mui.Box sx={{ ...detailCardSx, ...sx }}>
    {title ? (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Mui.Box>
          <Mui.Typography sx={designType.sectionHeading}>{title}</Mui.Typography>
          {helper ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{helper}</Mui.Typography> : null}
        </Mui.Box>
        {action}
      </Mui.Stack>
    ) : null}
    {children}
  </Mui.Box>
);

const FileCard = ({ file, onChange }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ ...detailCardSx, backgroundColor: designColors.slateBg }}>
    <Mui.Box sx={{ width: 40, height: 40, borderRadius: designRadii.iconButton, backgroundColor: "#E7F8EF", color: "#12B76A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <DescriptionOutlined />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>{file.name}</Mui.Typography>
      <Mui.Typography sx={designType.captionText}>{file.rows.length.toLocaleString("en-IN")} rows · {formatBytes(file.size)}</Mui.Typography>
    </Mui.Box>
    {onChange ? <SecondaryButton onClick={onChange} sx={{ minHeight: 36, minWidth: 0 }}>Change File</SecondaryButton> : null}
  </Mui.Stack>
);

const TabButtons = ({ tabs, tab, counts, onChange }) => (
  <Mui.Stack direction="row" spacing={1} sx={{ overflowX: "auto" }} role="tablist">
    {tabs.map((item) => {
      const active = item.key === tab;
      return (
        <Mui.ButtonBase
          key={item.key}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.key)}
          sx={{ px: 1.5, py: 0.75, flexShrink: 0, borderRadius: designRadii.control, border: `1px solid ${active ? designColors.tealDark : designColors.slate200}`, backgroundColor: active ? designColors.tealTint : designColors.white }}
        >
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: active ? designWeights.semibold : designWeights.medium, color: active ? designColors.tealDark : designColors.slate700, whiteSpace: "nowrap" }}>
            {item.label} ({counts[item.key].toLocaleString("en-IN")})
          </Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Stack>
);

const usePaged = (items, resetKey) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [resetKey]);
  return {
    pageItems: items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    footer: (
      <Mui.Box sx={{ px: 2, py: 1, borderTop: `1px solid ${designColors.slate200}` }}>
        {paginationFooter({
          page,
          rowsPerPage,
          total: items.length,
          itemLabel: "rows",
          onPageChange: setPage,
          onRowsPerPageChange: (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          },
          rowsPerPageOptions,
        })}
      </Mui.Box>
    ),
  };
};

// ---- Step 1: Upload ----

const UploadStep = ({ file, error, onFile, onTemplate, templateLoading }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (files) => {
    if (files?.[0]) onFile(files[0]);
  };

  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 1.5, alignItems: "stretch" }}>
      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <Mui.Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            pick(event.dataTransfer.files);
          }}
          sx={{
            ...detailCardSx,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 3,
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragging ? designColors.teal : designColors.slate300,
            backgroundColor: dragging ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.teal },
          }}
        >
          <input
            ref={inputRef}
            hidden
            type="file"
            accept=".xlsx,.xls,.csv"
            data-testid="ip-import-input"
            onChange={(event) => {
              pick(event.target.files);
              event.target.value = "";
            }}
          />
          <FileUploadOutlined sx={{ fontSize: 40, color: designColors.teal }} />
          <Mui.Typography sx={{ ...designType.sectionHeading, fontSize: 16, mt: 1.5 }}>Drag and drop your file here</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
            or <Mui.Box component="span" sx={{ color: designColors.tealDark, fontWeight: designWeights.semibold }}>click to browse</Mui.Box>
          </Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, mt: 2 }}>Supported formats: Excel (.xlsx, .xls), CSV (.csv) · Max 5 MB</Mui.Typography>
          <Mui.Button
            startIcon={templateLoading ? <Mui.CircularProgress size={16} /> : <FileDownloadOutlined />}
            disabled={templateLoading}
            onClick={(event) => {
              event.stopPropagation();
              onTemplate();
            }}
            sx={{ mt: 2, textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
          >
            Download IP Number Template
          </Mui.Button>
        </Mui.Box>
        {file ? <FileCard file={file} /> : null}
        {error ? <RequestInfoBanner tone="danger" title="Cannot use this file" body={error} /> : null}
      </Mui.Stack>

      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <RequestInfoBanner title="Template Information" body="Use the IP Number template to add or update ESI IP numbers for employees. The template includes the columns with 3 example rows." />
        <Card sx={{ flex: 1 }}>
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <DescriptionOutlined sx={{ fontSize: 20, color: designColors.tealDark }} />
            <Mui.Typography sx={designType.sectionHeading}>IP Number Import Guidelines</Mui.Typography>
          </Mui.Stack>
          <Mui.Stack spacing={1}>
            {GUIDELINES.map((line, index) => (
              <Mui.Stack key={line} direction="row" spacing={1.25} alignItems="flex-start">
                <Mui.Box sx={{ ...designType.captionText, flexShrink: 0, width: 20, height: 20, borderRadius: "50%", backgroundColor: designColors.slate100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {index + 1}
                </Mui.Box>
                <Mui.Typography sx={designType.helperText}>{line}</Mui.Typography>
              </Mui.Stack>
            ))}
          </Mui.Stack>
        </Card>
      </Mui.Stack>
    </Mui.Box>
  );
};

// ---- Step 2: Map columns ----

const MapStep = ({ file, mapping, onMappingChange, onReset, onChangeFile }) => {
  const missing = missingIpFields(mapping);
  const usedFields = new Set(Object.values(mapping));
  return (
    <Mui.Stack spacing={1.5}>
      <FileCard file={file} onChange={onChangeFile} />
      <Card
        title="Map File Columns"
        helper="Columns were matched automatically by header name — they can be in any order and renamed. Check each match and change it if needed."
        action={
          <Mui.Button startIcon={<Replay />} onClick={onReset} sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}>
            Reset Mapping
          </Mui.Button>
        }
      >
        <Mui.TableContainer sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>File Column (from Excel)</Mui.TableCell>
                <Mui.TableCell sx={tableHeadSx}>Map to System Field</Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {file.headers.map((header, index) => {
                const fieldKey = mapping[index] ?? "";
                return (
                  <Mui.TableRow key={`${header}-${index}`}>
                    <Mui.TableCell sx={{ ...cellSx, width: "45%" }}>{header || `Column ${index + 1}`}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      {/* A field already mapped to another column is left out. */}
                      <SelectField
                        value={fieldKey}
                        onChange={(value) => onMappingChange(index, value)}
                        options={[
                          { value: "", label: "Do not import" },
                          ...IP_IMPORT_FIELDS.filter((option) => !usedFields.has(option.key) || option.key === fieldKey).map((option) => ({
                            value: option.key,
                            label: `${option.label}${option.required ? " *" : ""}`,
                          })),
                        ]}
                        placeholder="Do not import"
                      />
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {missing.length ? (
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner tone="warning" title="Required fields not mapped" body={`Map a column to: ${missing.map((item) => item.label).join(", ")}.`} />
          </Mui.Box>
        ) : null}
      </Card>
    </Mui.Stack>
  );
};

// ---- Step 3: Preview & Import ----

const PREVIEW_TABS = [
  { key: "all", label: "All Rows" },
  { key: "valid", label: "Valid Rows" },
  { key: "error", label: "Error Rows" },
];

// Error rows are tinted red; clicking one opens the Edit panel.
const errorRowSx = {
  cursor: "pointer",
  backgroundColor: "#FEF2F2",
  "&:hover": { backgroundColor: "#FEE2E2" },
  "&:focus-visible": { outline: `2px solid ${errorText}`, outlineOffset: -2 },
};

const PreviewStep = ({ results, fields, unchanged, onFixRow }) => {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const valid = results.filter((result) => result.body);
  const invalid = results.filter((result) => !result.body);
  const counts = { all: results.length, valid: valid.length, error: invalid.length };

  const rows = useMemo(() => {
    const base = tab === "valid" ? valid : tab === "error" ? invalid : results;
    const needle = query.trim().toLowerCase();
    return needle ? base.filter((result) => Object.values(result.raw).some((value) => textOf(value).toLowerCase().includes(needle))) : base;
  }, [tab, query, results, valid, invalid]);
  const { pageItems, footer } = usePaged(rows, `${tab}:${query}`);

  return (
    <Mui.Stack spacing={1.5}>
      <Card title="Summary" helper={unchanged ? `${unchanged.toLocaleString("en-IN")} valid row(s) match the saved values and will be skipped.` : "Valid rows add or update the employee's IP number."}>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          {/* Cards pick the rows shown below, same as the tabs. */}
          <StatCard icon={StorageOutlined} title="Total Rows" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Valid Rows" value={counts.valid.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("valid")} isActive={tab === "valid"} activeBorder />
          <StatCard icon={CancelOutlined} title="Error Rows" value={counts.error.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("error")} isActive={tab === "error"} activeBorder />
        </Mui.Box>
        {invalid.length ? (
          <Mui.Box sx={{ mt: 1.5 }}>
            <RequestInfoBanner
              tone="warning"
              body={`${invalid.length.toLocaleString("en-IN")} row(s) have errors and will not be imported. Click a red row to correct it, or import only the valid rows.`}
            />
          </Mui.Box>
        ) : null}
      </Card>

      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 2 }}>
          <TabButtons tabs={PREVIEW_TABS} tab={tab} counts={counts} onChange={setTab} />
          <Mui.TextField
            size="small"
            placeholder="Search..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", md: 260 } }}
            InputProps={{ startAdornment: <Mui.InputAdornment position="start"><Search sx={{ fontSize: 18, color: designColors.slate400 }} /></Mui.InputAdornment> }}
          />
        </Mui.Stack>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>Row</Mui.TableCell>
                {fields.map((field) => <Mui.TableCell key={field.key} sx={tableHeadSx}>{field.label}</Mui.TableCell>)}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={fields.length + 1} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((result) => (
                  <Mui.TableRow
                    key={result.rowNumber}
                    {...(result.body
                      ? {}
                      : {
                          sx: errorRowSx,
                          tabIndex: 0,
                          title: "Click to correct this row",
                          onClick: () => onFixRow(result.rowNumber),
                          onKeyDown: (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onFixRow(result.rowNumber);
                            }
                          },
                        })}
                  >
                    <Mui.TableCell sx={cellSx}>{result.rowNumber}</Mui.TableCell>
                    {fields.map((field) => (
                      <Mui.TableCell key={field.key} sx={cellSx}>
                        {field.key === "ip_number" && result.body ? result.body.ip_number : shown(result.raw[field.key])}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Edit an error row (opened from the preview) ----

const COVERED_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// Edit panel inputs, in field order; only the mapped ones are shown.
const FIX_FIELDS = [
  { key: "employee_id", label: "Employee ID *", helper: "Employee code from the records.", placeholder: "e.g. VST1005" },
  { key: "employee_name", label: "Employee Name", helper: "For checking only. Not saved.", placeholder: "Employee name" },
  { key: "ip_number", label: "IP Number *", helper: "10-digit ESIC Insurance Number.", placeholder: "e.g. 3112345678" },
  { key: "dispensary", label: "Dispensary", helper: "ESI dispensary. Optional.", placeholder: "Dispensary (optional)" },
  { key: "covered_in_current_period", label: "Covered in Current Period", helper: "Go-live only. Optional.", placeholder: "System decides" },
];

// Side panel over the import: the row's errors on top, its values below to
// correct, then Re-Verify runs the browser check and the server's dry run
// for this row. A row that passes turns valid and the panel closes.
const RowFixPanel = ({ row, fields, onClose, onVerify }) => {
  const mappedKeys = new Set(fields.map((field) => field.key));
  const fixFields = FIX_FIELDS.filter((item) => mappedKeys.has(item.key));
  const [form, setForm] = useState(() => ({
    ...row.raw,
    covered_in_current_period: coveredValue(row.raw.covered_in_current_period) || "",
  }));
  const [errors, setErrors] = useState(row.errors);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  const verify = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await onVerify(row.rowNumber, form);
      if (next?.body) onClose();
      else if (next) setErrors(next.errors);
    } catch (error) {
      setErrors([`Could not check this row: ${error.message || "please try again."}`]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlobalSidePanel
      open
      onClose={busy ? undefined : onClose}
      hideExpand
      title="Edit IP Number Row"
      subtitle={`Row ${row.rowNumber}. Correct the values and re-verify.`}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel={busy ? "Verifying..." : "Re-Verify"}
          primaryProps={{ disabled: busy, loading: busy, onClick: verify }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        {errors.length ? (
          <RequestInfoBanner
            tone="danger"
            title={errors.length > 1 ? `${errors.length} errors in this row` : "Error in this row"}
            body={errors.map((message) => (
              <Mui.Box key={message} component="span" sx={{ display: "block" }}>• {message}</Mui.Box>
            ))}
          />
        ) : null}
        <FormSection title="IP Number" info="Values from the file. Saved to the employee's ESI details on import.">
          {fixFields.map((item, index) => (
            <Field key={item.key} label={item.label} helper={item.helper} wide={index === fixFields.length - 1 && fixFields.length % 2 === 1}>
              {item.key === "covered_in_current_period" ? (
                <SelectField value={form[item.key]} onChange={set(item.key)} options={COVERED_OPTIONS} placeholder={item.placeholder} />
              ) : (
                <TextField value={form[item.key]} onChange={set(item.key)} placeholder={item.placeholder} />
              )}
            </Field>
          ))}
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---- Result (server's answer per row) ----

const RESULT_TABS = [
  { key: "all", label: "All Rows" },
  { key: "success", label: "Imported" },
  { key: "failed", label: "Failed" },
];

const ResultStep = ({ result, showName }) => {
  const heads = ["Row", "Employee ID", ...(showName ? ["Employee"] : []), "IP Number", "Status", "Message"];
  const rows = result.rows;
  const counts = { all: rows.length, success: rows.filter((row) => row.ok).length, failed: rows.filter((row) => !row.ok).length };
  const [tab, setTab] = useState(counts.failed ? "failed" : "all");
  const list = tab === "all" ? rows : rows.filter((row) => (tab === "success" ? row.ok : !row.ok));
  const { pageItems, footer } = usePaged(list, tab);
  const { created, updated, unchanged } = result.summary;

  return (
    <Mui.Stack spacing={1.5}>
      <Card
        title={counts.failed ? "Import Finished With Errors" : "Import Complete"}
        helper={`${created.toLocaleString("en-IN")} added · ${updated.toLocaleString("en-IN")} updated · ${unchanged.toLocaleString("en-IN")} unchanged.`}
      >
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          <StatCard icon={StorageOutlined} title="Total Records" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Imported" value={counts.success.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("success")} isActive={tab === "success"} activeBorder />
          <StatCard icon={CancelOutlined} title="Failed" value={counts.failed.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("failed")} isActive={tab === "failed"} activeBorder />
        </Mui.Box>
      </Card>
      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Box sx={{ p: 2 }}>
          <TabButtons tabs={RESULT_TABS} tab={tab} counts={counts} onChange={setTab} />
        </Mui.Box>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                {heads.map((label) => <Mui.TableCell key={label} sx={tableHeadSx}>{label}</Mui.TableCell>)}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={heads.length} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((row) => (
                  <Mui.TableRow key={row.rowNumber}>
                    <Mui.TableCell sx={cellSx}>{row.rowNumber}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>{shown(row.raw.employee_id)}</Mui.TableCell>
                    {showName ? <Mui.TableCell sx={cellSx}>{shown(row.raw.employee_name)}</Mui.TableCell> : null}
                    <Mui.TableCell sx={cellSx}>{shown(row.ip_number)}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, fontWeight: designWeights.semibold, color: row.ok ? designColors.tealDark : errorText }}>{row.ok ? "Imported" : "Failed"}</Mui.TableCell>
                    <Mui.TableCell sx={{ ...cellSx, whiteSpace: "normal", color: row.ok ? undefined : errorText }}>{row.message}</Mui.TableCell>
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Wizard ----

// onImported(): runs after the server saved the rows, so the list reloads.
export const EsiIpImport = ({ open, domain, companyId, onClose, onImported }) => {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mapping, setMapping] = useState({});
  // Remembered for the next import in this visit (header -> field).
  const [rememberedMapping, setRememberedMapping] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);
  // Every file row after the browser check and the server's dry run
  // ({ rowNumber, raw, errors, body }). body = null for error rows.
  const [results, setResults] = useState([]);
  const [unchanged, setUnchanged] = useState(0);
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [result, setResult] = useState(null);
  const [fixRowNumber, setFixRowNumber] = useState(null);
  const fixRow = fixRowNumber == null ? null : results.find((item) => item.rowNumber === fixRowNumber) || null;

  useEffect(() => {
    if (!open) return;
    setStep(0); setFile(null); setFileError(""); setMapping({});
    setResults([]); setUnchanged(0); setValidateError("");
    setSaving(false); setSaveError(""); setResult(null); setFixRowNumber(null);
  }, [open]);

  const validRows = results.filter((item) => item.body);
  // Columns shown in the preview and Edit panel: only the mapped fields.
  const mappedFields = useMemo(() => {
    const mapped = new Set(Object.values(mapping));
    return IP_IMPORT_FIELDS.filter((field) => mapped.has(field.key));
  }, [mapping]);

  const mapFor = (headers) => {
    const remembered = {};
    headers.forEach((header, index) => {
      const key = rememberedMapping[String(header).trim().toLowerCase()];
      if (key) remembered[index] = key;
    });
    return Object.keys(remembered).length ? remembered : autoMapIpColumns(headers);
  };

  const handleFile = async (picked) => {
    setFileError("");
    const extension = picked.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setFile(null);
      setFileError("Unsupported file type. Upload an Excel (.xlsx, .xls) or CSV (.csv) file.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFile(null);
      setFileError("The file is larger than 5 MB.");
      return;
    }
    try {
      const [headerRow = [], ...body] = await readSheet(picked);
      const headers = headerRow.map((cell) => String(cell ?? "").trim());
      const rows = body.filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""));
      if (!headers.some(Boolean) || !rows.length) {
        setFile(null);
        setFileError("The file has no data rows under the header row.");
        return;
      }
      if (rows.length > MAX_ROWS) {
        setFile(null);
        setFileError(`The file has ${rows.length.toLocaleString("en-IN")} rows. Import up to 5,000 at a time.`);
        return;
      }
      setFile({ name: picked.name, size: picked.size, headers, rows });
      setMapping(mapFor(headers));
    } catch (error) {
      setFile(null);
      setFileError(error.message);
    }
  };

  // Template: column headers plus 3 example rows (covered employees of the
  // current period) to show the format.
  const downloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const { rows: samples } = await fetchEligibility({ domain, companyId, card: "covered", page: 1, pageSize: TEMPLATE_SAMPLE_ROWS });
      const sheet = XLSX.utils.aoa_to_sheet([
        IP_IMPORT_FIELDS.map((field) => field.label),
        ...samples.slice(0, TEMPLATE_SAMPLE_ROWS).map((row) => [row.emp_id, row.name, row.ip_number, "", ""]),
      ]);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "IP numbers");
      XLSX.writeFile(book, "ESI IP Number Import Template.xlsx");
    } catch (error) {
      showToast("error", error.message || "Couldn't build the template.");
    } finally {
      setTemplateLoading(false);
    }
  };

  // Map Columns > Next: browser check, then the server's dry run, then preview.
  const handleMapNext = async () => {
    setRememberedMapping(Object.fromEntries(Object.entries(mapping).map(([index, key]) => [file.headers[index].toLowerCase(), key])));
    const seen = new Set();
    const checked = file.rows.map((cells, index) => checkRow(index + 1, rawFromRow(cells, mapping), seen));
    setValidating(true);
    setValidateError("");
    try {
      const answer = await serverCheck({ domain, companyId, checked });
      setResults(answer.rows);
      setUnchanged(answer.summary?.unchanged || 0);
      setStep(2);
    } catch (error) {
      setValidateError(error.message || "Please try again.");
    } finally {
      setValidating(false);
    }
  };

  // Edit panel > Re-Verify: browser check against the other valid rows, then
  // the server's dry run for this one row. Throws when the server can't be
  // reached (the panel shows it).
  const verifyRow = async (rowNumber, raw) => {
    const seen = new Set(results.filter((item) => item.body && item.rowNumber !== rowNumber).map((item) => item.body.employee_code.toUpperCase()));
    let next = checkRow(rowNumber, raw, seen);
    if (next.body) next = (await serverCheck({ domain, companyId, checked: [next] })).rows[0];
    setResults((current) => current.map((item) => (item.rowNumber === rowNumber ? next : item)));
    return next;
  };

  const runImport = async () => {
    if (saving || !validRows.length) return;
    setSaving(true);
    setSaveError("");
    try {
      const answer = await serverCheck({ domain, companyId, checked: validRows, dryRun: false });
      setResult({
        summary: answer.summary,
        rows: validRows.map((item) => {
          const errors = answer.errorsByRow?.[item.rowNumber];
          return { rowNumber: item.rowNumber, raw: item.raw, ip_number: item.body.ip_number, ok: !errors, message: errors ? errors.join(" ") : "Saved" };
        }),
      });
      onImported?.();
    } catch (error) {
      setSaveError(error.message || "The import failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const canContinue = (step === 0 && Boolean(file)) || (step === 1 && missingIpFields(mapping).length === 0);

  const footer = result ? (
    <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
      <PrimaryButton onClick={onClose}>Done</PrimaryButton>
    </Mui.Stack>
  ) : (
    <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
      <SecondaryButton disabled={saving || validating} onClick={onClose}>Cancel</SecondaryButton>
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
        {step > 0 ? <SecondaryButton disabled={saving || validating} startIcon={<ArrowBack />} onClick={() => setStep(step - 1)}>Back</SecondaryButton> : null}
        {step < 2 ? (
          <PrimaryButton
            endIcon={<ArrowForward />}
            disabled={!canContinue || validating}
            loading={step === 1 && validating}
            onClick={step === 1 ? handleMapNext : () => setStep(step + 1)}
          >
            {step === 1 && validating ? "Validating..." : "Next"}
          </PrimaryButton>
        ) : (
          <Mui.Tooltip arrow placement="top" slotProps={tooltipStyleProps} title={!validRows.length ? "There are no valid rows to import." : ""}>
            <span>
              <PrimaryButton startIcon={<FileUploadOutlined />} disabled={!validRows.length || saving} loading={saving} onClick={runImport}>
                Import {validRows.length ? validRows.length.toLocaleString("en-IN") : ""}
              </PrimaryButton>
            </span>
          </Mui.Tooltip>
        )}
      </Mui.Stack>
    </Mui.Stack>
  );

  const subtitles = [
    "Upload a file to add or update ESI IP numbers for employees.",
    "Map your file columns to the IP number fields.",
    "Review your data and import.",
  ];

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={open}
      onToggleExpand={() => {}}
      hideExpand
      title="Import IP Numbers"
      subtitle={result ? "Import result for each record." : subtitles[step]}
      // Payroll -> Payroll home; ESI -> back to the list (closes this panel).
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!saving) history.push("/admin/payroll/setup"); } },
        "Statutory",
        { label: "ESI", onClick: () => { if (!saving) onClose(); } },
        "Import",
      ]}
      bodySx={{ backgroundColor: designColors.pageBg, p: { xs: 1.5, sm: 2 } }}
      footer={footer}
    >
      <Mui.Stack spacing={1.5}>
        {result ? (
          <ResultStep result={result} showName={mappedFields.some((field) => field.key === "employee_name")} />
        ) : (
          <>
            <Mui.Box sx={{ ...detailCardSx, py: 1 }}>
              <WizardStepper steps={STEPS} step={step} />
            </Mui.Box>
            {step === 0 ? (
              <UploadStep file={file} error={fileError} onFile={handleFile} onTemplate={downloadTemplate} templateLoading={templateLoading} />
            ) : step === 1 ? (
              <MapStep
                file={file}
                mapping={mapping}
                onMappingChange={(index, fieldKey) =>
                  setMapping((current) => {
                    const next = { ...current };
                    if (fieldKey) next[index] = fieldKey;
                    else delete next[index];
                    return next;
                  })
                }
                onReset={() => setMapping(autoMapIpColumns(file.headers))}
                onChangeFile={() => setStep(0)}
              />
            ) : (
              <PreviewStep results={results} fields={mappedFields} unchanged={unchanged} onFixRow={setFixRowNumber} />
            )}
          </>
        )}
        {validateError && !result ? <RequestInfoBanner tone="danger" title="Could not validate the rows" body={validateError} /> : null}
        {/* Import errors sit at the bottom, next to the Import button. */}
        {saveError ? <RequestInfoBanner tone="danger" title="Could not import IP numbers" body={saveError} /> : null}
      </Mui.Stack>
      {/* key: a fresh form for each row opened. */}
      {fixRow && step === 2 && !result ? <RowFixPanel key={fixRow.rowNumber} row={fixRow} fields={mappedFields} onClose={() => setFixRowNumber(null)} onVerify={verifyRow} /> : null}
    </GlobalSidePanel>
  );
};
